
import React, { Suspense } from 'react';
import { HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './components/AuthContext';
import { LeagueProvider } from './components/LeagueContext';
import { Layout } from './components/Layout';

const lazyPage = <T extends React.ComponentType<any>>(loader: () => Promise<any>, exportName: string) =>
    React.lazy(async () => ({ default: (await loader())[exportName] as T }));

const Login = lazyPage(() => import('./pages/Login'), 'Login');
const Register = lazyPage(() => import('./pages/Register'), 'Register');
const Profile = lazyPage(() => import('./pages/Profile'), 'Profile');
const Dashboard = lazyPage(() => import('./pages/Dashboard'), 'Dashboard');
const AdminWizard = lazyPage(() => import('./pages/AdminWizard'), 'AdminWizard');
const TicketSystem = lazyPage(() => import('./pages/TicketSystem'), 'TicketSystem');
const TeamsAdmin = lazyPage(() => import('./pages/TeamsAdmin'), 'TeamsAdmin');
const UserAdmin = lazyPage(() => import('./pages/UserAdmin'), 'UserAdmin');
const GridOverview = lazyPage(() => import('./pages/GridOverview'), 'GridOverview');
const Calendar = lazyPage(() => import('./pages/Calendar'), 'Calendar');
const Standings = lazyPage(() => import('./pages/Standings'), 'Standings');
const SeasonManager = lazyPage(() => import('./pages/SeasonManager'), 'SeasonManager');
const ResultsAdmin = lazyPage(() => import('./pages/ResultsAdmin'), 'ResultsAdmin');
const SeasonList = lazyPage(() => import('./pages/SeasonList'), 'SeasonList');
const SeasonCalendarEditor = lazyPage(() => import('./pages/SeasonCalendarEditor'), 'SeasonCalendarEditor');
const TracksAdmin = lazyPage(() => import('./pages/TracksAdmin'), 'TracksAdmin');
const StartingGrid = lazyPage(() => import('./pages/StartingGrid'), 'StartingGrid');
const PenaltyArchive = lazyPage(() => import('./pages/PenaltyArchive'), 'PenaltyArchive');
const NewsFeed = lazyPage(() => import('./pages/NewsFeed'), 'NewsFeed');
const NewsEditor = lazyPage(() => import('./pages/NewsEditor'), 'NewsEditor');
const Heatmap = lazyPage(() => import('./pages/Heatmap'), 'Heatmap');
const DriverProfile = lazyPage(() => import('./pages/DriverProfile'), 'DriverProfile');
const TeamProfile = lazyPage(() => import('./pages/TeamProfile'), 'TeamProfile');
const AttendanceAdmin = lazyPage(() => import('./pages/AttendanceAdmin'), 'AttendanceAdmin');
const Statistics = lazyPage(() => import('./pages/Statistics'), 'Statistics');
const BadgeAdmin = lazyPage(() => import('./pages/BadgeAdmin'), 'BadgeAdmin');
const HallOfFame = lazyPage(() => import('./pages/HallOfFame'), 'HallOfFame');
const GraphicsCenter = lazyPage(() => import('./pages/GraphicsCenter'), 'GraphicsCenter');
const RulesAdmin = lazyPage(() => import('./pages/RulesAdmin'), 'RulesAdmin');
const TelemetryMonitor = lazyPage(() => import('./pages/TelemetryMonitor'), 'TelemetryMonitor');
const Streams = lazyPage(() => import('./pages/Streams'), 'Streams');
const EventDetails = lazyPage(() => import('./pages/EventDetails'), 'EventDetails');
const FileEditor = lazyPage(() => import('./pages/FileEditor'), 'FileEditor');
const TelemetryToolAdmin = lazyPage(() => import('./pages/TelemetryToolAdmin'), 'TelemetryToolAdmin');
const TelemetryDownload = lazyPage(() => import('./pages/TelemetryDownload'), 'TelemetryDownload');
const DriverNumbers = lazyPage(() => import('./pages/DriverNumbers'), 'DriverNumbers');
const WebAdminBugs = lazyPage(() => import('./pages/WebAdminBugs'), 'WebAdminBugs');
const Rules = lazyPage(() => import('./pages/Rules'), 'Rules');
const SystemConsole = lazyPage(() => import('./pages/SystemConsole'), 'SystemConsole');
const StewardChat = lazyPage(() => import('./pages/StewardChat'), 'StewardChat');
const NavigationAdmin = lazyPage(() => import('./pages/NavigationAdmin'), 'NavigationAdmin');
const DiscordMonitor = lazyPage(() => import('./pages/DiscordMonitor'), 'DiscordMonitor');
const PollAdmin = lazyPage(() => import('./pages/PollAdmin'), 'PollAdmin');
const PredictionGame = lazyPage(() => import('./pages/PredictionGame'), 'PredictionGame');
const VoteAdmin = lazyPage(() => import('./pages/VoteAdmin'), 'VoteAdmin');
const CasterGraphics = lazyPage(() => import('./pages/CasterGraphics'), 'CasterGraphics');
const TestStudio = lazyPage(() => import('./pages/TestStudio'), 'TestStudio');
const AdminContext = lazyPage(() => import('./pages/AdminContext'), 'AdminContext');
const CasterComments = lazyPage(() => import('./pages/CasterComments'), 'CasterComments');
const BetaResults = lazyPage(() => import('./pages/BetaResults'), 'BetaResults');
const ChampionshipCalculator = lazyPage(() => import('./pages/ChampionshipCalculator'), 'ChampionshipCalculator');
const RaceAnalytics = lazyPage(() => import('./pages/RaceAnalytics'), 'RaceAnalytics');
const History = lazyPage(() => import('./pages/History'), 'History');
const VideoUpload = lazyPage(() => import('./pages/VideoUpload'), 'VideoUpload');
const ReserveStats = lazyPage(() => import('./pages/ReserveStats'), 'ReserveStats');
const StewardTickets = lazyPage(() => import('./pages/StewardTickets'), 'StewardTickets');

const PageLoader = () => (
    <div className="flex min-h-[38vh] items-center justify-center" role="status" aria-live="polite">
        <div className="flex items-center gap-3 rounded-2xl border border-white/[0.07] bg-white/[0.025] px-5 py-3 text-xs font-bold uppercase tracking-[0.14em] text-white/45">
            <span className="h-2 w-2 animate-pulse rounded-full bg-f1-red" />
            Seite wird geladen
        </div>
    </div>
);

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user, isLoading } = useAuth();
    if (isLoading) return null;
    if (!user) return <Navigate to="/login" />;
    return <Layout><Suspense fallback={<PageLoader />}>{children}</Suspense></Layout>;
};

const AppRoutes = () => (
    <Routes>
        <Route path="/login" element={<Suspense fallback={<PageLoader />}><Login /></Suspense>} />
        <Route path="/register" element={<Suspense fallback={<PageLoader />}><Register /></Suspense>} />
        <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/news" element={<ProtectedRoute><NewsFeed /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        <Route path="/calendar" element={<ProtectedRoute><Calendar /></ProtectedRoute>} />
        <Route path="/standings" element={<ProtectedRoute><Standings /></ProtectedRoute>} />
        <Route path="/statistics" element={<ProtectedRoute><Statistics /></ProtectedRoute>} />
        <Route path="/streams" element={<ProtectedRoute><Streams /></ProtectedRoute>} />
        <Route path="/heatmap" element={<ProtectedRoute><Heatmap /></ProtectedRoute>} />
        <Route path="/hall-of-fame" element={<ProtectedRoute><HallOfFame /></ProtectedRoute>} />
        <Route path="/driver/:id" element={<ProtectedRoute><DriverProfile /></ProtectedRoute>} />
        <Route path="/team/:id" element={<ProtectedRoute><TeamProfile /></ProtectedRoute>} />
        <Route path="/event/:id" element={<ProtectedRoute><EventDetails /></ProtectedRoute>} />
        <Route path="/telemetry" element={<ProtectedRoute><TelemetryMonitor /></ProtectedRoute>} />
        <Route path="/grid" element={<ProtectedRoute><GridOverview /></ProtectedRoute>} />
        <Route path="/history" element={<ProtectedRoute><History /></ProtectedRoute>} />
        <Route path="/tickets" element={<ProtectedRoute><TicketSystem /></ProtectedRoute>} />
        <Route path="/tickets/archive" element={<ProtectedRoute><PenaltyArchive /></ProtectedRoute>} />
        <Route path="/steward-chat" element={<ProtectedRoute><StewardChat /></ProtectedRoute>} /> 
        <Route path="/rules" element={<ProtectedRoute><Rules /></ProtectedRoute>} /> 
        <Route path="/predictions" element={<ProtectedRoute><PredictionGame /></ProtectedRoute>} />
        
        {/* CASTER SPECIFIC */}
        <Route path="/caster/graphics" element={<ProtectedRoute><CasterGraphics /></ProtectedRoute>} />

        {/* ADMIN ROUTES */}
        <Route path="/admin/news/create" element={<ProtectedRoute><NewsEditor /></ProtectedRoute>} />
        <Route path="/admin/news/edit/:id" element={<ProtectedRoute><NewsEditor /></ProtectedRoute>} />
        
        <Route path="/admin/wizard" element={<ProtectedRoute><AdminWizard /></ProtectedRoute>} />
        <Route path="/admin/teams" element={<ProtectedRoute><TeamsAdmin /></ProtectedRoute>} />
        <Route path="/admin/tracks" element={<ProtectedRoute><TracksAdmin /></ProtectedRoute>} />
        <Route path="/admin/users" element={<ProtectedRoute><UserAdmin /></ProtectedRoute>} />
        <Route path="/admin/grid" element={<ProtectedRoute><SeasonManager /></ProtectedRoute>} />
        <Route path="/admin/seasons" element={<ProtectedRoute><SeasonList /></ProtectedRoute>} />
        <Route path="/admin/seasons/:id/calendar" element={<ProtectedRoute><SeasonCalendarEditor /></ProtectedRoute>} />
        <Route path="/admin/starting-grid" element={<ProtectedRoute><StartingGrid /></ProtectedRoute>} />
        <Route path="/admin/results" element={<ProtectedRoute><ResultsAdmin /></ProtectedRoute>} />
        <Route path="/admin/attendance" element={<ProtectedRoute><AttendanceAdmin /></ProtectedRoute>} />
        <Route path="/admin/badges" element={<ProtectedRoute><BadgeAdmin /></ProtectedRoute>} />
        <Route path="/admin/graphics" element={<ProtectedRoute><GraphicsCenter /></ProtectedRoute>} />
        <Route path="/admin/rules" element={<ProtectedRoute><RulesAdmin /></ProtectedRoute>} />
        <Route path="/admin/files" element={<ProtectedRoute><FileEditor /></ProtectedRoute>} />
        <Route path="/admin/telemetry-upload" element={<ProtectedRoute><TelemetryToolAdmin /></ProtectedRoute>} />
        <Route path="/admin/telemetry-download" element={<ProtectedRoute><TelemetryDownload /></ProtectedRoute>} />
        <Route path="/admin/numbers" element={<ProtectedRoute><DriverNumbers /></ProtectedRoute>} />
        <Route path="/admin/bugs" element={<ProtectedRoute><WebAdminBugs /></ProtectedRoute>} />
        <Route path="/admin/console" element={<ProtectedRoute><SystemConsole /></ProtectedRoute>} />
        <Route path="/admin/navigation" element={<ProtectedRoute><NavigationAdmin /></ProtectedRoute>} />
        <Route path="/admin/discord" element={<ProtectedRoute><DiscordMonitor /></ProtectedRoute>} />
        <Route path="/admin/polls" element={<ProtectedRoute><PollAdmin /></ProtectedRoute>} />
        <Route path="/admin/votes" element={<ProtectedRoute><VoteAdmin /></ProtectedRoute>} /> 
        <Route path="/admin/test-studio" element={<ProtectedRoute><TestStudio /></ProtectedRoute>} />
        <Route path="/admin/context" element={<ProtectedRoute><AdminContext /></ProtectedRoute>} />
        <Route path="/admin/beta-results" element={<ProtectedRoute><BetaResults /></ProtectedRoute>} />
        <Route path="/caster/comments" element={<ProtectedRoute><CasterComments /></ProtectedRoute>} />
        <Route path="/caster/calculator" element={<ProtectedRoute><ChampionshipCalculator /></ProtectedRoute>} />
        <Route path="/race-analytics" element={<ProtectedRoute><RaceAnalytics /></ProtectedRoute>} />
        <Route path="/video-upload" element={<ProtectedRoute><VideoUpload /></ProtectedRoute>} />
        <Route path="/admin/reserve-stats" element={<ProtectedRoute><ReserveStats /></ProtectedRoute>} />
        <Route path="/steward/tickets" element={<ProtectedRoute><StewardTickets /></ProtectedRoute>} />
        
        <Route path="*" element={<ProtectedRoute><div className="text-center text-gray-500 mt-20">Page Not Found</div></ProtectedRoute>} />
    </Routes>
);

const App: React.FC = () => {
  return (
    <AuthProvider>
        <LeagueProvider>
            <HashRouter>
                <AppRoutes />
            </HashRouter>
        </LeagueProvider>
    </AuthProvider>
  );
};

export default App;
